import {
  AttachmentMetaDataType,
  AttachmentType,
} from '../../../../../shared/editor/types';
import { Schedule } from '../../../../settings/types/schedule';
import { Template } from '../template-modal/types';
import { FromEmailAccount, ToProspect } from './components/sidebar/types';

export type StateValues = {
  title: string;
  subject: string;
  content: string;
  email: string;
  preheader: string;
  attachments?: string;
};

export type StateErrors = { [P in keyof StateValues]: string };
export type StateDirty = { [P in keyof StateErrors]: boolean };

export type SubjectEmailSuggestion = {
  subjectPercentage: number;
  subjectStatus: string;
  subjectLength: number;
};

export type WordEmailSuggestion = {
  wordPercentage: number;
  wordStatus: string;
  wordLength: number;
};

export type PersonalizationEmailSuggestion = {
  personalizationPercentage: number;
  personalizationStatus: string;
  personalizationLength: number;
};

export type LinkEmailSuggestion = {
  linkPercentage: number;
  linkStatus: string;
  linkLength: number;
};

export type SpammyEmailSuggestion = {
  spammyPercentage: number;
  spammyStatus: string;
  spammyLength: number;
  spammyWords: string[];
};

export type IState = {
  values: StateValues;
  errors: StateErrors;
  dirty: StateDirty;
  subjectEmailSuggestion: SubjectEmailSuggestion;
  wordEmailSuggestion: WordEmailSuggestion;
  personalizationEmailSuggestion: PersonalizationEmailSuggestion;
  linkEmailSuggestion: LinkEmailSuggestion;
  spammyEmailSuggestion: SpammyEmailSuggestion;
  sendAutomatically: boolean;
  sendTestEmailVisible: boolean;
  skipFocusOnEditorOnce: boolean;
  selectedTemplate?: Template;
  showTemplates: boolean;
  showTitle: boolean;
  showSendTestEmailModal: boolean;
  showEmailDisconnectedModal: boolean;
  showEmailNotConnectedModal: boolean;
  showEmailNotAddedModal: boolean;
  showConfirmationModal: boolean;
  showPreheaderInput: boolean;
  showPreheaderRecommendation: boolean;
  shouldInitiateAChat: boolean;
  attachments: AttachmentType[];
  attachmentsMeta: AttachmentMetaDataType[];
  attachmentsSizeInBytes: number;
  showHTMLContentDetectedModal: boolean;
  skipHTMLContentDetection: boolean;
  showSidebarFooter: boolean;
  selectedProspectInEmailPreview: ToProspect;
  selectedEmailInEmailPreview: FromEmailAccount;
  sendThisEmailInSameThreadCheckbox: boolean;
  sequenceSchedule: Schedule;
  disabledVariableTags: string[];
  showGlobalRestrictionModal: boolean;
  showGlobalRestrictionModalForTest?: boolean;
  showInboxTestModal: boolean;
  stylingPreference: StylingPreference;
  showStylingDetectedActionBar: boolean;
  isTemplateApplied: boolean;
  hasAutoRemovedStylingOnce?: boolean;
};

export enum StylingPreference {
  KeepStyling = 'keepStyling',
  RemoveStyling = 'removeStyling',
}

export type TemplateDropDownOption = {
  name: string;
  key: number;
  isOwn: boolean;
};

export enum PreheaderErrorConditions {
  ErrorLength = 150,
  RecommendationLength = 130,
}
export const EmailSuggestionStatus = {
  TOO_SHORT: 'Too short',
  SHORT: 'Short',
  IDEAL: 'Ideal',
  LENGTHY: 'Lengthy',
  TOO_LENGTHY: 'Too lengthy',
  POOR: 'Poor',
  AVERAGE: 'Average',
  GOOD: 'Good',
  EXCELLENT: 'Excellent',
};

export const EmailSuggestionClasses = {
  [EmailSuggestionStatus.TOO_SHORT]: 'too-short',
  [EmailSuggestionStatus.SHORT]: 'short',
  [EmailSuggestionStatus.IDEAL]: 'ideal',
  [EmailSuggestionStatus.LENGTHY]: 'lengthy',
  [EmailSuggestionStatus.TOO_LENGTHY]: 'too-lengthy',
  [EmailSuggestionStatus.POOR]: 'poor',
  [EmailSuggestionStatus.AVERAGE]: 'average',
  [EmailSuggestionStatus.GOOD]: 'good',
  [EmailSuggestionStatus.EXCELLENT]: 'excellent',
};

export type TinyMceEditorInputProps = {
  inputValue: string;
  inputErrorMessage?: string;
  placeholder?: string;
  isGeneratingWithAi?: boolean;
  disableEditMode?: any;
  onSubjectKeyUp?: () => void;
  onInputBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  setError?: any;
  onInputChange: (
    value: any,
    e: {
      target: {
        name: string;
        value: any;
      };
    },
  ) => void;
  mergeTagOptions?: any;
  sendSpintaxMixpanelEvent?: () => void;
  showTitle?: string;
  enableMergeTag?: boolean;
  enableSpintax?: boolean;
  enableEmoji?: boolean;
  inputRecommendation?: string;
  showInputCount?: number | string;
  showInput?: boolean;
  name?: string;
  inputClassName?: string;
  showInlineCount?: boolean;
  maxInputCount?: number;
  getEditorInstance?: (editor: any) => void;
  error?: boolean;
  onTemplateApplied?: boolean;
  onTemplateAppliedReset?: () => void;
  sendThisEmailInSameThreadCheckboxValue?: boolean;
  showDivider?: boolean;
  isTitleDisabled?: boolean;
};
